import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../utils/api';
import { handleApiError, setupErrorHandling } from '../utils/errorHandler';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isParent, setIsParent] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Logout function that can be called from anywhere
  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setIsParent(false);
    setIsAdmin(false);
  };

  const checkParentStatus = async () => {
    try {
      const response = await api.get('/is-parent');
      setIsParent(response.data.is_parent || false);
      setIsAdmin(response.data.is_admin || false);
    } catch (error) {
      console.error('Error checking parent status:', error);
      setIsParent(false);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Set default authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser({ token });
      // Check if user is a parent/admin - wait for it to complete before setting loading to false
      checkParentStatus().finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }

    // Set up global error handling
    const cleanupErrorHandling = setupErrorHandling(logout);

    // Cleanup on unmount
    return cleanupErrorHandling;
  }, []);

  const login = async (username, password) => {
    try {
      const response = await api.post('/login', { username, password });
      const { access_token } = response.data;

      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser({ token: access_token });
      
      // Check if user is a parent after login
      await checkParentStatus();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: handleApiError(error, 'Login failed')
      };
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await api.post('/register', { username, email, password });
      const { access_token } = response.data;

      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser({ token: access_token });
      
      // Check if user is a parent after registration
      await checkParentStatus();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: handleApiError(error, 'Registration failed')
      };
    }
  };

  const isAuthenticated = !!user;

  const value = {
    user,
    login,
    register,
    logout,
    isAuthenticated,
    isParent,
    isAdmin,
    loading,
    checkParentStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
